import { useQuery } from "@tanstack/react-query";
import { store } from "../../store/useStore";
import axios from "../../libs/axios";
import inventory from "../../libs/axios/inventory";

export function useGetAllAmbassadors({
  page,
  per_page,
  params,
  enabled
}: {
  page?: number;
  per_page?: number;
  params?: any;
  enabled?: boolean
}) {
  return useQuery({
    queryKey: ["ambassador", per_page, page, params],
    queryFn: async () => {
      const { data } = await axios.get("/public/ambassadors", {
        params: {
          page,
          per_page,
          ...params,
        },
      });
      return data?.data;
    },
    refetchOnWindowFocus: false,
    enabled: enabled
  });
}
export function useGetCountriesByRegion({
  number_only,
}: {
  number_only?: boolean;
}) {
  const active_device = store.auth.activeDeviceIdentifier();
  return useQuery({
    queryKey: ["countries-by-region"],
    queryFn: async () => {
      const { data } = await axios.get(`/dropdowns/countries`, {
        params: {
          number_only: number_only,
        },
        headers: {
          "fi-device-number": active_device,
        },
      });
      return data?.data;
    },
  });
}
export function useGetCitiesByState({
  params,
  enabled,
}: {
  params?: any;
  enabled?: boolean;
}) {
  const active_device = store.auth.activeDeviceIdentifier();

  return useQuery({
    queryKey: ["cities-by-state", params],
    queryFn: async () => {
      const { data } = await axios.get(`/dropdowns/cities`, {
        params: {
          ...params,
        },
        headers: {
          "fi-device-number": active_device,
        },
      });
      return data?.data;
    },
    enabled: enabled,
  });
}
export function useGetDiscipline({
  params,
  enabled,
  text_only,
}: {
  params?: any;
  enabled?: boolean;
  text_only?: boolean;
}) {
  const active_device = store.auth.activeDeviceIdentifier();

  return useQuery({
    queryKey: ["discipline"],
    queryFn: async () => {
      const { data } = await axios.get(`/dropdowns/disciplines`, {
        params: {
          ...params,
          text_only: text_only,
        },
        headers: {
          "fi-device-number": active_device,
        },
      });
      return data?.data;
    },
    enabled: enabled,
  });
}
export function useApplicationCampusPrograms({
  enabled,
  campus_id,
  params,
}: {
  enabled?: boolean;
  campus_id: string;
  params: any;
}) {
  const active_device = store.auth.activeDeviceIdentifier();

  return useQuery({
    queryKey: ["applications", "campus", campus_id, "programs"],
    queryFn: async () => {
      const { data } = await axios.get(`/dropdowns/programs`, {
        params: {
          campus_id,
          ...params,
        },
        headers: {
          "fi-device-number": active_device,
        },
      });

      const modifiedData = await data?.data.map((item: any) => ({
        ...item,
        value: JSON.stringify(item.value),
      }));

      return modifiedData;
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    enabled: enabled ?? true,
  });
}
export function useApplicationCampus({
  enabled,
  institution_id,
}: {
  enabled?: boolean;
  institution_id: string;
}) {
  const active_device = store.auth.activeDeviceIdentifier();

  return useQuery({
    queryKey: ["applications", "campuses"],
    queryFn: async () => {
      const { data } = await axios.get(
        `/dropdowns/campuses?institution_id=${institution_id}`,
        {
          headers: {
            "fi-device-number": active_device,
          },
        }
      );

      const modifiedData = await data?.data.map((item: any) => ({
        ...item,
        value: JSON.stringify(item.value), // Convert 'value' to string
      }));

      return modifiedData;
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    enabled: enabled ?? true,
  });
}
export function useGetAllInstitutions({
  number_only,
}: {
  number_only: boolean;
}) {
  const active_device = store.auth.activeDeviceIdentifier();

  return useQuery({
    queryKey: ["institution", "dropdowns"],
    queryFn: async () => {
      const { data } = await axios.get("/dropdowns/institutions", {
        params: {
          number_only,
        },
        headers: {
          "fi-device-number": active_device,
        },
      });

      const institutions = data.data.recordsInfo.map((institution: any) => {
        return {
          label: institution.label,
          value: String(institution.value),
        };
      });

      return institutions;
    },
    refetchOnWindowFocus: false,
    staleTime: 400,
    gcTime: 400,
  });
}
export function useGetStatesByCountry({
  enabled = true,
  country_id,
  params,
}: {
  enabled: boolean;
  country_id?: string;
  params?: any;
}) {
  const active_device = store.auth.activeDeviceIdentifier();

  return useQuery({
    queryKey: ["states_by_country", country_id],
    queryFn: async () => {
      const { data } = await axios.get("/dropdowns/states", {
        params: {
          country_id,
          ...params,
        },
        headers: {
          "fi-device-number": active_device,
        },
      });

      return data;
    },
    refetchOnWindowFocus: false,
    enabled,
  });
}
export function useGetStudyLevel() {
  const active_device = store.auth.activeDeviceIdentifier();

  return useQuery({
    queryKey: ["study-level"],
    queryFn: async () => {
      const { data } = await inventory.get("/dropdown/studylevel", {
        headers: {
          "fi-device-number": active_device,
        },
      });

      const studyLevel = data.data.map((studyLevel: any) => {
        return {
          label: studyLevel.label,
          value: String(studyLevel.value),
        };
      });

      return studyLevel;
    },
    refetchOnWindowFocus: false,
    staleTime: 400,
    gcTime: 400,
  });
}

